/**
 * Datas de negócio = strings 'YYYY-MM-DD' (date-only) e meses 'YYYY-MM'.
 * NUNCA usar `new Date('YYYY-MM-DD')` (parse UTC desloca o dia). Toda aritmética aqui é sobre strings.
 * Ver CLAUDE.md § Datas.
 */

export type Ymd = string; // 'YYYY-MM-DD'
export type Ym = string; // 'YYYY-MM'

const MONTHS_PT = [
  'Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho',
  'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro',
] as const;

// en-CA formata como YYYY-MM-DD
const SP_YMD_FORMATTER = new Intl.DateTimeFormat('en-CA', {
  timeZone: 'America/Sao_Paulo',
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
});

/** Data de hoje em America/Sao_Paulo como 'YYYY-MM-DD'. Única fonte de "hoje". */
export function todaySP(): Ymd {
  return SP_YMD_FORMATTER.format(new Date());
}

/**
 * Converte um instante (timestamp ISO com hora, ex. de `createdAt`) para
 * 'YYYY-MM-DD' em America/Sao_Paulo. NUNCA usar `iso.slice(0, 10)` nesses
 * campos — pega a data em UTC e desloca +1 dia para cadastros/eventos
 * feitos entre 21h e 23h59 no horário de Brasília.
 */
export function toDateSP(iso: string): Ymd {
  return SP_YMD_FORMATTER.format(new Date(iso));
}

/** Mês atual em America/Sao_Paulo como 'YYYY-MM'. */
export function currentMonthSP(): Ym {
  return monthOf(todaySP());
}

/** '2026-07-15' -> '2026-07' */
export function monthOf(date: Ymd): Ym {
  return date.slice(0, 7);
}

const pad = (n: number): string => String(n).padStart(2, '0');

export function parseYm(ym: Ym): { year: number; month: number } {
  const [y, m] = ym.split('-');
  return { year: Number(y), month: Number(m) };
}

export function parseYmd(date: Ymd): { year: number; month: number; day: number } {
  const [y, m, d] = date.split('-');
  return { year: Number(y), month: Number(m), day: Number(d) };
}

export function formatYm(year: number, month: number): Ym {
  return `${year}-${pad(month)}`;
}

export function formatYmd(year: number, month: number, day: number): Ymd {
  return `${year}-${pad(month)}-${pad(day)}`;
}

/** Dias no mês (month 1-12). Cobre ano bissexto. */
export function daysInMonth(year: number, month: number): number {
  return new Date(Date.UTC(year, month, 0)).getUTCDate();
}

/** Dia efetivo: clampa 29-31 em meses curtos. effDay(2026, 2, 31) -> 28. */
export function effDay(year: number, month: number, day: number): number {
  return Math.min(day, daysInMonth(year, month));
}

/** Soma n meses a um 'YYYY-MM' (n pode ser negativo). */
export function addMonths(ym: Ym, n: number): Ym {
  const { year, month } = parseYm(ym);
  const zero = year * 12 + (month - 1) + n;
  return formatYm(Math.floor(zero / 12), (zero % 12) + 1);
}

export function nextMonth(ym: Ym): Ym {
  return addMonths(ym, 1);
}

export function prevMonth(ym: Ym): Ym {
  return addMonths(ym, -1);
}

/** Soma n meses a uma data 'YYYY-MM-DD', clampando o dia (31/01 +1 mês -> 28/02). */
export function addMonthsToDate(date: Ymd, n: number): Ymd {
  const { year, month, day } = parseYmd(date);
  const targetYm = addMonths(formatYm(year, month), n);
  const { year: ty, month: tm } = parseYm(targetYm);
  return formatYmd(ty, tm, effDay(ty, tm, day));
}

/** Subtrai 1 dia de uma data 'YYYY-MM-DD' (usa UTC internamente para não deslocar). */
export function prevDay(date: Ymd): Ymd {
  const { year, month, day } = parseYmd(date);
  const dt = new Date(Date.UTC(year, month - 1, day - 1));
  return formatYmd(dt.getUTCFullYear(), dt.getUTCMonth() + 1, dt.getUTCDate());
}

/** '2026-07' -> 'Julho 2026' */
export function formatMonthLabel(ym: Ym): string {
  const { year, month } = parseYm(ym);
  return `${MONTHS_PT[month - 1]} ${year}`;
}

/** '2026-07' -> 'julho' (minúsculo, p/ pill do dashboard) */
export function monthName(ym: Ym): string {
  const { month } = parseYm(ym);
  return MONTHS_PT[month - 1]!.toLowerCase();
}

/** '2026-07-15' -> '15/07/2026' */
export function formatDateBR(date: Ymd): string {
  const { year, month, day } = parseYmd(date);
  return `${pad(day)}/${pad(month)}/${year}`;
}

/** Lista de N meses terminando em `end` (inclusive). rangeOfMonths('2026-07', 6) -> [...,'2026-07']. */
export function rangeOfMonths(end: Ym, count: number): Ym[] {
  const out: Ym[] = [];
  for (let i = count - 1; i >= 0; i--) out.push(addMonths(end, -i));
  return out;
}
